import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import MainLayout from '../../components/MainLayout';
import { useTranslation } from '../../context/LanguageContext';
import StepNav from './StepNav';
import ProductSelector from './ProductSelector';
import {
  defaultWeights,
  fusionThreshold,
  modalityMeta,
  degradationInfo,
  modalityThreshold,
  judgeModality,
  modalityJudgeMeta,
  getProduct,
  defaultDrilldownShotId,
} from '../../mock/moduleb';
import './moduleb-common.css';
import './ModuleBFusion.css';

const MODALITIES = ['vision', 'process', 'thermal'];

// 반원 게이지 (0~1)
function Gauge({ value, color, size = 96 }) {
  const r = size / 2 - 8;
  const cx = size / 2;
  const cy = size / 2;
  const circ = Math.PI * r; // 반원 둘레
  const offset = circ * (1 - value);
  return (
    <svg width={size} height={size / 2 + 10} className="mb-gauge">
      <path d={`M ${cx - r},${cy} A ${r},${r} 0 0 1 ${cx + r},${cy}`} fill="none" stroke="#2a2f38" strokeWidth="8" strokeLinecap="round" />
      <path
        d={`M ${cx - r},${cy} A ${r},${r} 0 0 1 ${cx + r},${cy}`}
        fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={offset}
      />
    </svg>
  );
}

function ModuleBFusion() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const shotId = searchParams.get('shot') || defaultDrilldownShotId;
  const product = getProduct(shotId);
  const modalityScores = product.scores;

  // 가중치는 사전에 정해진 고정값 (사용자가 직접 조정하지 않음)
  const weights = defaultWeights;
  const [active, setActive] = useState({ vision: true, process: true, thermal: true });

  // Graceful degradation: 비활성 모달리티 가중치 0 후 재정규화
  const effective = useMemo(() => {
    const raw = {
      vision: active.vision ? weights.alpha : 0,
      process: active.process ? weights.beta : 0,
      thermal: active.thermal ? weights.gamma : 0,
    };
    const sum = raw.vision + raw.process + raw.thermal || 1;
    const norm = {
      vision: raw.vision / sum,
      process: raw.process / sum,
      thermal: raw.thermal / sum,
    };
    const S =
      norm.vision * modalityScores.vision +
      norm.process * modalityScores.process +
      norm.thermal * modalityScores.thermal;
    const contrib = MODALITIES.map((m) => ({
      key: m,
      labelKey: modalityMeta[m].labelKey,
      color: modalityMeta[m].color,
      value: norm[m] * modalityScores[m],
      weight: norm[m],
    }));
    const activeCount = Object.values(active).filter(Boolean).length;
    return { S, contrib, activeCount, norm };
  }, [weights, active, modalityScores]);

  const isNG = effective.S >= fusionThreshold;

  return (
    <MainLayout>
      <div className="mb-container">
        <StepNav current={3} shotId={shotId} />

        <div className="mb-page-header">
          <div>
            <div className="mb-page-title">
              <span className="mb-step-tag">STEP 3</span>{t('pages.moduleb.nav.steps.fusion.title')}
              <span className="mb-product-chip">
                <span className="material-symbols-outlined">inventory_2</span>
                {product.productId}
              </span>
            </div>
            <div className="mb-page-subtitle">
              {t('pages.moduleb.fusion.subtitle')}
            </div>
          </div>
          <ProductSelector value={shotId} onChange={(id) => setSearchParams({ shot: id })} />
        </div>

        <div className="mb-body">
          {/* 융합식 */}
          <div className="fusion-formula">
            <span className="fusion-formula-eq">
              S = <em style={{ color: modalityMeta.vision.color }}>α·Sᵥ</em>
              <span className="fusion-plus">+</span>
              <em style={{ color: modalityMeta.process.color }}>β·Sₚ</em>
              <span className="fusion-plus">+</span>
              <em style={{ color: modalityMeta.thermal.color }}>γ·Sₜ</em>
            </span>
            <span className="fusion-formula-note">{t('pages.moduleb.fusion.formulaNote')}</span>
          </div>

          <div className="fusion-grid">
            {/* 좌: 모달리티 점수 카드 + 가중치 슬라이더 */}
            <div className="mb-card">
              <div className="mb-card-title">
                <span className="material-symbols-outlined">fact_check</span>{t('pages.moduleb.fusion.modalityTitle')}
              </div>

              {MODALITIES.map((m) => {
                const meta = modalityMeta[m];
                const wKey = meta.weightKey;
                const verdict = judgeModality(modalityScores[m]);
                const vMeta = modalityJudgeMeta[verdict];
                return (
                  <div key={m} className={`fusion-modality ${!active[m] ? 'inactive' : ''}`}>
                    <div className="fusion-modality-head">
                      <span className="fusion-modality-name">
                        <span className="sync-dot" style={{ background: meta.color }} />
                        {t(meta.labelKey)} <span className="fusion-symbol">{meta.symbol}</span>
                      </span>
                      <span className="fusion-modality-right">
                        {active[m] && (
                          <span className="mb-badge fusion-modality-verdict" style={{ color: vMeta.color, background: vMeta.bg }}>
                            {t(vMeta.labelKey)}
                          </span>
                        )}
                        <span className="fusion-modality-score" style={{ color: meta.color }}>
                          {active[m] ? modalityScores[m].toFixed(2) : '—'}
                        </span>
                      </span>
                    </div>
                    {/* 불량 점수 바 + 판정 임계선 */}
                    <div className="mb-bar-track fusion-score-bar" style={{ marginBottom: 10 }}>
                      <div className="mb-bar-fill" style={{ width: `${(active[m] ? modalityScores[m] : 0) * 100}%`, background: active[m] && verdict === 'NG' ? '#ef4444' : meta.color }} />
                      <div className="fusion-score-threshold" style={{ left: `${modalityThreshold * 100}%` }} />
                    </div>
                    {/* 고정 가중치 (읽기 전용) */}
                    <div className="fusion-weight-row fixed">
                      <span className="fusion-weight-symbol" style={{ color: meta.color }}>{meta.weightSymbol}</span>
                      <span className="fusion-weight-fixed-label">{t('pages.moduleb.fusion.fixedWeight')}</span>
                      <div className="fusion-weight-bar">
                        <div className="fusion-weight-bar-fill" style={{ width: `${weights[wKey] * 100}%`, background: meta.color }} />
                      </div>
                      <span className="fusion-weight-val">{(weights[wKey] * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                );
              })}

              <div className="fusion-weight-sum">
                <span className="material-symbols-outlined fusion-weight-lock">lock</span>
                {t('pages.moduleb.fusion.weightSum')}
                <span className="fusion-weight-sum-note">{t('pages.moduleb.fusion.weightSumNote')}</span>
              </div>
            </div>

            {/* 중: 기여도 도넛 */}
            <div className="mb-card fusion-contrib">
              <div className="mb-card-title">
                <span className="material-symbols-outlined">donut_large</span>{t('pages.moduleb.fusion.contribTitle')}
              </div>
              <div className="fusion-donut">
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart>
                    <Pie
                      data={effective.contrib.filter((c) => c.value > 0)}
                      dataKey="value" nameKey="label"
                      innerRadius={55} outerRadius={82} paddingAngle={2}
                      stroke="none"
                    >
                      {effective.contrib.filter((c) => c.value > 0).map((c) => (
                        <Cell key={c.key} fill={c.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="fusion-donut-center">
                  <div className="fusion-donut-label">{t('pages.moduleb.fusion.donutLabel')}</div>
                  <div className="fusion-donut-value">{effective.S.toFixed(3)}</div>
                </div>
              </div>
              <div className="fusion-contrib-legend">
                {effective.contrib.map((c) => (
                  <div key={c.key} className="fusion-contrib-item">
                    <span className="fusion-contrib-chip" style={{ background: c.color, opacity: c.value > 0 ? 1 : 0.3 }} />
                    <span className="fusion-contrib-name">{t(c.labelKey)}</span>
                    <span className="fusion-contrib-val">{c.value > 0 ? c.value.toFixed(3) : '—'}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 우: 최종 S 게이지 + 임계값 */}
            <div className="mb-card fusion-result">
              <div className="mb-card-title">
                <span className="material-symbols-outlined">speed</span>{t('pages.moduleb.fusion.resultTitle')}
              </div>
              <div className="fusion-gauge-wrap">
                <Gauge value={effective.S} color={isNG ? '#ef4444' : '#22c55e'} size={180} />
                <div className="fusion-gauge-value" style={{ color: isNG ? '#ef4444' : '#22c55e' }}>
                  {effective.S.toFixed(3)}
                </div>
                <div className="fusion-gauge-range"><span>0.0</span><span>1.0</span></div>
              </div>
              <div className="fusion-threshold">
                <span>{t('pages.moduleb.fusion.thresholdLabel')}</span>
                <strong>{fusionThreshold.toFixed(2)}</strong>
              </div>
              <div className={`fusion-verdict ${isNG ? 'ng' : 'ok'}`}>
                {isNG ? t('pages.moduleb.fusion.verdictNg') : t('pages.moduleb.fusion.verdictOk')}
              </div>
              <button
                type="button"
                className="fusion-next-btn"
                onClick={() => navigate(`/module-b/judgment?shot=${shotId}`)}
              >
                <span>{t('pages.moduleb.fusion.next', { product: product.productId })}</span>
                <span className="material-symbols-outlined">arrow_forward</span>
              </button>
            </div>
          </div>

          {/* Graceful Degradation */}
          <div className="mb-card mb-mt">
            <div className="mb-card-title">
              <span className="material-symbols-outlined">rule</span>
              {t('pages.moduleb.fusion.degradeTitle')}
              <span className="fusion-active-badge">
                {t('pages.moduleb.fusion.activeCount', { n: effective.activeCount })}
              </span>
            </div>
            <div className="fusion-degrade">
              <div className="fusion-toggles">
                {MODALITIES.map((m) => {
                  const meta = modalityMeta[m];
                  return (
                    <button
                      key={m}
                      type="button"
                      className={`fusion-toggle ${active[m] ? 'on' : 'off'}`}
                      onClick={() => setActive((p) => ({ ...p, [m]: !p[m] }))}
                    >
                      <span className="material-symbols-outlined">{active[m] ? 'toggle_on' : 'toggle_off'}</span>
                      <span className="sync-dot" style={{ background: meta.color }} />
                      {t(meta.labelKey)} ({meta.symbol})
                      <span className="fusion-toggle-state">{active[m] ? t('pages.moduleb.fusion.stateActive') : t('pages.moduleb.fusion.stateMissing')}</span>
                    </button>
                  );
                })}
              </div>

              <div className="fusion-degrade-info">
                {!active.thermal && (
                  <span className="mb-badge fusion-degrade-badge">
                    <span className="material-symbols-outlined">auto_mode</span>
                    {t('pages.moduleb.fusion.degradeBadge')}
                  </span>
                )}
                <div className="fusion-norm-list">
                  {MODALITIES.map((m) => (
                    <div key={m} className="fusion-norm-item">
                      <span>{t(modalityMeta[m].labelKey)}</span>
                      <span className="fusion-norm-val" style={{ color: active[m] ? modalityMeta[m].color : '#4b5563' }}>
                        {(effective.norm[m] * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
                <p className="fusion-degrade-note">{t(degradationInfo.noteKey)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default ModuleBFusion;
